/**
 * An update's review on the page (L11's update; part 24d log, principles 1, 3–5, tables U and F): what the book holds
 * when the update begins, read for the planner (`holdingsOf`); after the bundle is staged, each chapter both have decided
 * from the two histories (`chapterUpdate`) and merged where both moved on (`mergeUpdate`), each value with no history
 * offered unticked, each share both hold merged (part 38b, table BU); and the request Apply writes, built from the review
 * and the offers the writer ticked. The store is
 * read through the workspace one short read at a time, so nothing waits behind the review's merges.
 */
import { METADATA_MERGE, METADATA_TEXT, metadataFromJson, serializeMetadata, serializeTheme, themeFromJson } from '@cockatrice/layout';
import type { BookMetadata } from '@cockatrice/layout';
import type { MergedChapter } from '@cockatrice/core';
import { chapterUpdate, mergeShares, textHash } from '@cockatrice/export/bundle';
import type { AddedChapter, BookTexts, HistoryItem, MatchedChapter, NoteOffer, UpdateCounts, UpdateHoldings } from '@cockatrice/export/bundle';
import { StoreRecordError } from '@cockatrice/storage';
import type { BookRecord, ChapterEntry, ImportOp, Reviewed, ShareRecord, Snapshot, Store, UpdatedText, UpdateRequest } from '@cockatrice/storage';
import type { SourcesChapter } from '../docx/protocol.ts';
import { cleanTitle } from '../shell/workspace.ts';
import { bundleTitle } from './book.ts';

/** Runs one read of the store, in the workspace's queue; null when no store is open or the read failed. */
export type StoreRead = <T>(read: (store: Store) => Promise<T>) => Promise<T | null>;

/** U5: chapters merged three ways off the page (the DOCX worker's M″5), one result each, in order; null when that failed. */
export type SourcesMerge = (chapters: readonly SourcesChapter[]) => Promise<readonly MergedChapter[] | null>;

/** What a store read of `key` gives: the value, undefined for none, or `refused` for a record its reader refuses. */
async function readOrRefused<T>(read: () => Promise<T | undefined>): Promise<T | undefined | 'refused'> {
  try {
    return await read();
  } catch (error) {
    if (error instanceof StoreRecordError) return 'refused';
    throw error;
  }
}

/** The keys of every record of `found`, readable or refused. */
function keysOf(found: { readonly records: readonly { readonly id: string }[]; readonly invalid: readonly { readonly key: unknown }[] }): Set<string> {
  return new Set([...found.records.map((record) => record.id), ...found.invalid.map((record) => record.key).filter((key) => typeof key === 'string')]);
}

/** What an update of a book reads of it when it begins (S′1): its record (null: none, or refused), and what it holds. */
export interface UpdateTarget {
  readonly book: BookRecord | null;
  readonly holdings: UpdateHoldings;
}

/**
 * S′1, principle 3: what book `bookId` holds, every store's IDs readable or refused, through its `bookId` indexes and its
 * image key range; null when nothing of it is here any more (`gone`).
 */
export async function holdingsOf(store: Store, bookId: string): Promise<UpdateTarget | null> {
  if (!(await store.hasBook(bookId))) return null;
  const record = await readOrRefused(() => store.read('books', bookId));
  const entries = await store.readWhere('entries', 'bookId', bookId);
  const notes = await store.readWhere('notes', 'bookId', bookId);
  const images = await store.readImages(bookId);
  const shares = await store.readWhere('shares', 'bookId', bookId);
  return {
    book: record === 'refused' ? null : (record ?? null),
    holdings: {
      entries: new Map(entries.records.map((entry) => [entry.id, entry])),
      refusedEntries: keysOf({ records: [], invalid: entries.invalid }),
      snapshots: keysOf(await store.readWhere('snapshots', 'bookId', bookId)),
      copies: keysOf(await store.readWhere('conflictCopies', 'bookId', bookId)),
      notes: new Map(notes.records.map((note) => [note.id, note])),
      refusedNotes: keysOf({ records: [], invalid: notes.invalid }),
      // Part 38d, V6-5: the book's reading links, readable or refused.
      shareLinks: keysOf(await store.readWhere('shareLinks', 'bookId', bookId)),
      // Part 38b, BU: the book's shares, readable by ID to merge, refused by key to keep.
      shares: new Map(shares.records.map((share) => [share.id, share])),
      refusedShares: keysOf({ records: [], invalid: shares.invalid }),
      images: keysOf({ records: images.records, invalid: images.invalid }),
      imageBytes: images.records.reduce((sum, image) => sum + image.size, 0),
    },
  };
}

/** N2, N3: whether any record holds chapter ID `id`: an entry or a chapter text, of any book, readable or refused. */
export async function chapterHeld(store: Store, id: string): Promise<boolean> {
  for (const name of ['entries', 'chapters'] as const) if ((await readOrRefused(() => store.read(name, id))) !== undefined) return true;
  return false;
}

/** What a chapter both have gets in the review (table U). */
export type ChapterOutcome =
  /** U3: the bundle's text, an ordinary edit. */
  | 'taken'
  /** U5: merged three ways; this device's text kept as a copy. */
  | 'merged'
  /** U6, U7: the bundle's text; this device's kept as a copy. */
  | 'copied'
  /** U4: kept, newer here. */
  | 'newer-here'
  /** U2: the same. */
  | 'unchanged'
  /** U5's MG7 (deep-fix r1): the merge gives this device's text; the bundle's, which differs, kept in the history. */
  | 'in-history'
  /** U1: the bundle holds no text for it. */
  | 'no-text'
  /** U8: this device's entry or text can't be read. */
  | 'unreadable'
  /** G′3 (deep-fix r2): deleted forever here since the update began; the bundle's text kept as a conflict copy. */
  | 'gone';

/** A chapter both have, as the review shows it and Apply writes it. */
export interface PlannedChapter {
  readonly id: string;
  /** Its title on this device. */
  readonly title: string;
  readonly outcome: ChapterOutcome;
  /** This device's text as read (U11), and the text Apply writes: the same when nothing changes. */
  readonly before: string;
  readonly after: string;
  /** U5: regions both changed, the bundle's taken (MG4). */
  readonly conflicts: number;
  /**
   * Table B′ (deep-fix r1): the bundle's text, kept in the chapter's history as the update's own snapshot (`history`, with
   * the chapter's staging ID it is made under) when Apply doesn't write it whole and this device's history doesn't hold
   * it (U5, U8); or, the chapter gone here (G′3, deep-fix r2), as a conflict copy of it (`copy`); null otherwise.
   */
  readonly kept: { readonly staging: string; readonly text: string; readonly as: 'history' | 'copy' } | null;
}

/**
 * Tables F and F′ (deep-fix r2): a value the bundle has with no history to merge it on, offered unticked by `key`: what
 * the box shows of the bundle's (`value`), and what taking it writes (`written`: this device's free text kept below the
 * bundle's, F′2–F′6).
 */
export type UpdateOffer =
  | { readonly kind: 'title'; readonly key: string; readonly chapterId: string; readonly chapter: string; readonly value: string }
  | { readonly kind: 'synopsis'; readonly key: string; readonly chapterId: string; readonly chapter: string; readonly value: string; readonly written: string }
  /** Part 19e: `chapter` the title of the chapter the note sits on, or null in the Notebook. */
  | { readonly kind: 'note'; readonly key: string; readonly note: NoteOffer; readonly chapter: string | null; readonly value: string }
  | { readonly kind: 'detail'; readonly key: string; readonly unit: keyof BookMetadata; readonly fields: readonly (keyof BookMetadata)[]; readonly value: string; readonly written: BookMetadata; readonly free: boolean }
  | { readonly kind: 'theme'; readonly key: string; readonly value: string };

/** What the review shows (table S′, S′3) and what Apply writes from. */
export interface UpdateReview {
  readonly bookId: string;
  /** The book's title on this device. */
  readonly title: string;
  readonly chapters: readonly PlannedChapter[];
  readonly added: readonly AddedChapter[];
  /** F6: chapters only this device has, kept as they are. */
  readonly onlyHere: number;
  readonly counts: UpdateCounts;
  readonly offers: readonly UpdateOffer[];
  /** U11: each chapter's entry as read, and the book's record, for the offers' `before`. */
  readonly entries: ReadonlyMap<string, ChapterEntry>;
  readonly book: BookRecord | null;
  /** F′4–F′6: the book's details as part 05 reads its record, which taken details are written over; null for none. */
  readonly details: BookMetadata | null;
  /** N1: the staged chapter IDs' hand-over. */
  readonly staging: readonly (readonly [string, string])[];
  /** BU (part 38b): each share both hold, as stored now and merged with the bundle's; none where the merge changes nothing. */
  readonly shares: readonly Reviewed<ShareRecord>[];
}

/** What {@link planUpdate} reads the staged update from. */
export interface StagedUpdate {
  readonly bookId: string;
  readonly matched: readonly MatchedChapter[];
  readonly added: readonly AddedChapter[];
  readonly noteOffers: readonly NoteOffer[];
  /** BU (part 38b): the bundle's shares the book held when the update began, as the book's records (`shareMerges`). */
  readonly shareMerges: readonly ShareRecord[];
  readonly bookTexts: BookTexts;
  readonly counts: UpdateCounts;
  readonly staging: readonly (readonly [string, string])[];
}

/** F4: `text` as `read` takes it, re-serialised by `write` as the comparison's form; null when `read` refuses it. */
function readable<T>(text: string, read: (text: string) => T, write: (value: T) => string): string | null {
  try {
    return write(read(text));
  } catch {
    return null;
  }
}

/** U5: a chapter whose merge is still to run: what it merges, and where its outcome goes. */
interface PendingMerge {
  readonly id: string;
  readonly title: string;
  readonly staging: string;
  readonly merge: SourcesChapter;
}

/**
 * U2–U8: one chapter both have, read from the store now (`text`: undefined for no record, null when it can't be read) and
 * decided; a merge (U5) is left to run with the others, off the page.
 */
async function planChapter(read: StoreRead, digest: (bytes: Uint8Array) => Promise<Uint8Array>, chapter: MatchedChapter, entry: ChapterEntry | undefined, text: string | undefined | null): Promise<PlannedChapter | PendingMerge> {
  const title = entry?.title ?? chapter.title;
  const kept = (outcome: ChapterOutcome, before: string, history: string | null = null, as: 'history' | 'copy' = 'history'): PlannedChapter => ({
    id: chapter.id,
    title,
    outcome,
    before,
    after: before,
    conflicts: 0,
    kept: history === null ? null : { staging: chapter.staging, text: history, as },
  });
  // G′3 (deep-fix r2): its entry gone since the update began; its history went with it, so the bundle's text is a copy.
  if (entry === undefined && !chapter.refused) return kept('gone', '', chapter.text, 'copy');
  // B′ (deep-fix r1): nothing of this device's is read, so the bundle's text goes into the history.
  if (entry === undefined || chapter.refused || text === null) return kept('unreadable', '', chapter.text);
  // U9: no text record reads as empty.
  const current = text ?? '';
  if (chapter.text === null) return kept('no-text', current);
  const bundle = chapter.text;
  // U2, before any history is read.
  if (bundle === current) return kept('unchanged', current);
  const snapshots: readonly Snapshot[] = (await read((store) => store.readWhere('snapshots', 'chapterId', chapter.id)))?.records ?? [];
  // Principle 4: this device's history, each snapshot whose text reads; the update's staged ones name staging IDs.
  const history: (HistoryItem & { readonly text: string })[] = [];
  for (const snapshot of snapshots) {
    const shot = await read((store) => store.read('snapshotTexts', snapshot.id));
    if (shot !== null && shot !== undefined) history.push({ id: snapshot.id, createdAt: snapshot.createdAt, seq: snapshot.seq, sha256: await textHash(digest, shot.text), text: shot.text });
  }
  const decided = chapterUpdate({ bundleHash: await textHash(digest, bundle), bundleHashes: chapter.hashes, currentHash: await textHash(digest, current), history });
  switch (decided.kind) {
    case 'newer-here':
      return kept('newer-here', current);
    case 'take':
      return { id: chapter.id, title, outcome: 'taken', before: current, after: bundle, conflicts: 0, kept: null };
    case 'two-way':
      return { id: chapter.id, title, outcome: 'copied', before: current, after: bundle, conflicts: 0, kept: null };
    case 'merge':
      return { id: chapter.id, title, staging: chapter.staging, merge: { base: decided.base.text, current, file: bundle } };
  }
}

/**
 * S′3, tables U and F: the review of `staged` against book `staged.bookId` as the store holds it now: each chapter both
 * have decided (U2–U8), the merges run together by `merge` (U5), each differing value with no history offered (F2, F4,
 * F5); null when the book can't be read, `failed` when the merges couldn't run.
 */
export async function planUpdate(read: StoreRead, digest: (bytes: Uint8Array) => Promise<Uint8Array>, merge: SourcesMerge, staged: StagedUpdate): Promise<UpdateReview | null | 'failed'> {
  const { bookId } = staged;
  const target = await read((store) => holdingsOf(store, bookId));
  if (target === null) return null;
  const { entries } = target.holdings;
  const planned: (PlannedChapter | PendingMerge)[] = [];
  const offers: UpdateOffer[] = [];
  for (const chapter of staged.matched) {
    const entry = entries.get(chapter.id);
    // A read that failed (null) is as unreadable as a refused record: nothing is decided on it; none stored is U9's.
    const stored = await read((store) => readOrRefused(() => store.read('chapters', chapter.id)));
    planned.push(await planChapter(read, digest, chapter, entry, stored === null || stored === 'refused' ? null : stored?.text));
    if (entry === undefined || chapter.refused) continue;
    // F′1: the bundle's title cleaned as a stored one is (23b's CS8); F′8: an empty value is no offer.
    const title = cleanTitle(chapter.title);
    if (title !== entry.title && title !== '') offers.push({ kind: 'title', key: `title:${chapter.id}`, chapterId: chapter.id, chapter: entry.title, value: title });
    // F′2: free text, this device's kept below the bundle's (a chapter's notes are notes since part 19e: MB8).
    const value = chapter.synopsis;
    if (value !== entry.synopsis && value !== '') offers.push({ kind: 'synopsis', key: `synopsis:${chapter.id}`, chapterId: chapter.id, chapter: entry.title, value, written: both(value, entry.synopsis) });
  }
  // F5 (deep-fix r1), F′3, MB8, MB9: each note offered against the note as stored now, not as the update began: one gone,
  // deleted or not the writer's, or that now says what the bundle's does, is no offer; taken, its title and group are the
  // bundle's, its text the bundle's above this one.
  for (const { after: theirs } of staged.noteOffers) {
    const before = target.holdings.notes.get(theirs.id);
    if (before?.deletedAt !== null || before.origin !== 'writer') continue;
    if (before.title === theirs.title && before.group === theirs.group && before.text === theirs.text) continue;
    const text = before.text === theirs.text ? theirs.text : both(theirs.text, before.text);
    const chapter = before.chapterId === null ? null : (entries.get(before.chapterId)?.title ?? '');
    offers.push({ kind: 'note', key: `note:${before.id}`, note: { before, after: { ...before, title: theirs.title, group: theirs.group, text } }, chapter, value: theirs.text });
  }
  // BU (part 38b): each share merged with the record as stored now, not as the update began: one gone or refused since
  // keeps what is stored; one the merge leaves as it is writes nothing.
  const shares: Reviewed<ShareRecord>[] = [];
  for (const theirs of staged.shareMerges) {
    const before = target.holdings.shares.get(theirs.id);
    if (before === undefined) continue;
    const after = mergeShares(before, theirs);
    if (JSON.stringify(after) !== JSON.stringify(before)) shares.push({ before, after });
  }
  const { book } = target;
  const details = book === null ? null : readDetails(book.metadata);
  if (book !== null) {
    // F′4–F′6: the bundle's details (its book piece's, the first text), unit by unit, against the book's; F′9: neither
    // unreadable.
    for (const text of staged.bookTexts.metadata.slice(0, 1)) {
      const theirs = readDetails(text);
      if (theirs !== null && details !== null) offers.push(...detailOffers(theirs, details));
    }
    for (const text of staged.bookTexts.theme.slice(0, 1)) {
      const theirs = readable(text, themeFromJson, serializeTheme);
      if (theirs !== null && theirs !== readable(book.theme, themeFromJson, serializeTheme)) offers.push({ kind: 'theme', key: 'theme', value: text });
    }
  }
  // U5: every merge in one job off the page, its outcomes in order; MG7's (the merge gives this device's text) unchanged.
  const pending = planned.filter((each): each is PendingMerge => 'merge' in each);
  const merged = pending.length === 0 ? [] : await merge(pending.map((each) => each.merge));
  if (merged === null) return 'failed';
  const outcomes = new Map(pending.map((each, at) => [each, merged[at]]));
  const chapters = planned.map((each): PlannedChapter => {
    if (!('merge' in each)) return each;
    const result = outcomes.get(each);
    const { current, file } = each.merge;
    // B′ (deep-fix r1): a merge writes the blocks it compares, never what it doesn't (notes nothing cites, later
    // definitions, link definitions, how marks are written), so the bundle's text goes into the history either way.
    return result?.changed === true
      ? { id: each.id, title: each.title, outcome: 'merged', before: current, after: result.text, conflicts: result.conflicts, kept: { staging: each.staging, text: file, as: 'history' } }
      : { id: each.id, title: each.title, outcome: 'in-history', before: current, after: current, conflicts: 0, kept: { staging: each.staging, text: file, as: 'history' } };
  });
  const matched = new Set(staged.matched.map((chapter) => chapter.id));
  return {
    bookId,
    title: bundleTitle(book?.metadata ?? null, ''),
    chapters,
    added: staged.added,
    onlyHere: [...entries.keys()].filter((id) => !matched.has(id)).length,
    counts: staged.counts,
    offers,
    entries,
    book,
    details,
    staging: staged.staging,
    shares,
  };
}

/** F′2, F′3, F′5: a free text taken: the bundle's, then this device's below a blank line (just the bundle's for none here). */
function both(theirs: string, ours: string): string {
  return ours === '' ? theirs : `${theirs}\n\n${ours}`;
}

/** F′9: the details `text` holds as part 05 reads them, or null. */
function readDetails(text: string): BookMetadata | null {
  try {
    return metadataFromJson(text);
  } catch {
    return null;
  }
}

/** `to` with its field `field` taken from `from`. */
function withField(to: BookMetadata, from: BookMetadata, field: keyof BookMetadata): BookMetadata {
  return { ...to, [field]: from[field] };
}

/** A detail's value in words: its text, a year, or nothing (the cover, named by its box). */
function shownDetail(value: BookMetadata[keyof BookMetadata]): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

/**
 * Table F′, F′4–F′6, F′8 (deep-fix r2): the bundle's details `theirs` offered against this device's `ours`, one box per
 * part 05 merge unit that differs: a last-writer-wins unit's value replaced; a keep-both unit's free text the bundle's above
 * this device's, the epigraph with this device's source on a line under its quote; none for an empty value of the
 * bundle's, or a pair past the field's length.
 */
function detailOffers(theirs: BookMetadata, ours: BookMetadata): UpdateOffer[] {
  const offers: UpdateOffer[] = [];
  for (const unit of METADATA_MERGE) {
    if (unit.fields.every((field) => JSON.stringify(theirs[field]) === JSON.stringify(ours[field]))) continue;
    const key = `detail:${unit.fields.join('+')}`;
    if (unit.rule === 'last-writer-wins') {
      for (const field of unit.fields) {
        const value = theirs[field];
        if (value !== '' && value !== null) offers.push({ kind: 'detail', key, unit: field, fields: unit.fields, value: shownDetail(value), written: withField(ours, theirs, field), free: false });
      }
    } else if (unit.fields.includes('epigraph')) {
      const kept = ours.epigraphSource === '' ? ours.epigraph : `${ours.epigraph}\n— ${ours.epigraphSource}`;
      const epigraph = both(theirs.epigraph, ours.epigraph === '' ? '' : kept);
      const value = theirs.epigraphSource === '' ? theirs.epigraph : `${theirs.epigraph}\n— ${theirs.epigraphSource}`;
      if (theirs.epigraph !== '' && epigraph.length <= METADATA_TEXT.epigraph.max) {
        offers.push({ kind: 'detail', key, unit: 'epigraph', fields: unit.fields, value, written: { ...ours, epigraph, epigraphSource: theirs.epigraphSource }, free: true });
      }
    } else {
      for (const field of FREE_DETAILS) {
        const written = both(theirs[field], ours[field]);
        if (unit.fields.includes(field) && theirs[field] !== '' && written.length <= METADATA_TEXT[field].max) {
          offers.push({ kind: 'detail', key, unit: field, fields: unit.fields, value: theirs[field], written: { ...ours, [field]: written }, free: true });
        }
      }
    }
  }
  return offers;
}

/** F′5: the keep-both details of one field. */
const FREE_DETAILS = ['dedication', 'acknowledgements', 'aboutAuthor', 'alsoBy'] as const;

/**
 * S′5: what Apply writes for `review` with the offers `ticked` (by key) taken: each changed text (a copy kept for U5–U7,
 * but of an empty text, U9),
 * each chapter entry with its taken fields, each taken note, the book record with its taken metadata and theme, each
 * merged share (BU), and `ops` (the recovered chapters' records, made under the staging book).
 */
export function updateRequest(review: UpdateReview, ticked: ReadonlySet<string>, ops: readonly ImportOp[]): UpdateRequest {
  const texts: UpdatedText[] = review.chapters.flatMap((chapter) => (chapter.after === chapter.before ? [] : [{ id: chapter.id, before: chapter.before, after: chapter.after, copy: chapter.outcome !== 'taken' && chapter.before !== '' }]));
  const taken = review.offers.filter((offer) => ticked.has(offer.key));
  const fields = new Map<string, Partial<ChapterEntry>>();
  let book: BookRecord | null = null;
  let details = review.details;
  for (const offer of taken) {
    if (offer.kind === 'title') fields.set(offer.chapterId, { ...fields.get(offer.chapterId), title: offer.value });
    else if (offer.kind === 'synopsis') fields.set(offer.chapterId, { ...fields.get(offer.chapterId), synopsis: offer.written });
    else if (offer.kind === 'detail' && details !== null) details = offer.fields.reduce((to, field) => withField(to, offer.written, field), details);
    else if (offer.kind === 'theme' && review.book !== null) book = { ...(book ?? review.book), theme: offer.value };
  }
  // F′4–F′6: the taken details written over the book's, as part 05 writes them.
  if (details !== review.details && details !== null && review.book !== null) book = { ...(book ?? review.book), metadata: serializeMetadata(details) };
  const entries: Reviewed<ChapterEntry>[] = [];
  for (const [id, change] of fields) {
    const before = review.entries.get(id);
    if (before !== undefined) entries.push({ before, after: { ...before, ...change } });
  }
  return {
    bookId: review.bookId,
    chapters: review.staging,
    texts,
    entries,
    notes: taken.flatMap((offer) => (offer.kind === 'note' ? [offer.note] : [])),
    shares: review.shares,
    book: book === null || review.book === null ? null : { before: review.book, after: book },
    // G′2, G′4 (deep-fix r3): the chapters whose entry the review read; one refused or gone then is decided on nothing.
    present: review.chapters.flatMap((chapter) => (review.entries.has(chapter.id) ? [chapter.id] : [])),
    ops,
  };
}
